/**
 * Global event bus for app-wide event broadcasting and listening.
 *
 * Provides access to the application's event system for cross-component communication.
 * Events are global and shared across the entire application.
 *
 * @returns Object with broadcast/dispatch and listen/handle methods for global events
 *
 * @example
 * const events = useAppEvent();
 * events.dispatch('user:logout', userId);
 * events.handle('user:logout', (userId) => { ... });
 */
export const useAppEvent = () => {
  const nuxtApp = useNuxtApp();
  return {
    broadcast: nuxtApp.$broadcast,
    listen: nuxtApp.$listen,
    listenMany: nuxtApp.$listenMany,
    listenOnce: nuxtApp.$listenOnce,

    // aliases
    dispatch: nuxtApp.$broadcast,
    handle: nuxtApp.$listen,
    handleMany: nuxtApp.$listenMany,
    handleOnce: nuxtApp.$listenOnce,
  };
};

/**
 * Namespaced event bus for scoped event communication.
 *
 * Wraps the global event system with a namespace prefix to avoid event name collisions.
 * All event names are automatically prefixed with the provided namespace.
 *
 * Use this when you need to isolate events to a specific feature or module
 * while still allowing cross-component communication within that scope.
 *
 * @param namespace - Prefix to prepend to all event names
 * @returns Object with namespaced handle, handleMany, and dispatch methods
 *
 * @example
 * const events = useNamespacedEvent<{ save: (data: any) => void }>('product');
 * events.dispatch('save', data); // Actually dispatches 'productsave'
 * events.handle('save', (data) => { ... }); // Listens to 'productsave'
 */
export const useNamespacedEvent = <T extends Record<string, any>>(
  namespace: string
) => {
  type E = keyof T & string;

  const events = useAppEvent();
  const n = (action: E) => {
    return `${namespace}:${action}`;
  };

  const handle = <A extends E>(action: A, handler: T[A]) => {
    events.listen(n(action), handler);
  };

  const handleMany = (listeners: Partial<T>) => {
    const l = Object.entries(listeners).reduce((acc, [k, h]) => {
      acc[n(k)] = h;
      return acc;
    }, {} as any);
    events.listenMany(l);
  };

  const dispatch = <A extends E>(name: A, ...a: Parameters<T[A]>) => {
    return events.broadcast(n(name), ...a);
  };

  return {
    handle,
    handleMany,
    dispatch,
  };
};

/**
 * Isolated event bus with auto-generated unique namespace.
 *
 * Creates a namespaced event system with a random UUID namespace, ensuring
 * complete isolation from all other event handlers in the application.
 *
 * Perfect for component-local event communication where you need an event bus
 * but want to guarantee no collisions with other components or features.
 *
 * @returns Namespaced event object with unique UUID namespace
 *
 * @example
 * const events = useIsolatedEvent();
 * events.dispatch('update', data); // Only handlers in this instance will receive it
 * events.handle('update', (data) => { ... });
 */
export const useIsolatedEvent = <T extends Record<string, any>>() => {
  return useNamespacedEvent<T>(uuid());
};
